import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Box, Typography, TextField, Button, Paper, Grid, Avatar, Switch, FormControlLabel, Divider, Alert } from '@mui/material';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const ProfilePage = () => {
    const { user, refreshUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { showToast } = useToast();

    // Form State
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [photoUrl, setPhotoUrl] = useState('');
    const [notifications, setNotifications] = useState({
        newCourse: true,
        evaluationOpen: true,
        evaluationDue: true,
        evaluationGraded: true
    });

    // Read-only data
    const [profileData, setProfileData] = useState<any>(null);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            // Fetch user details to get latest data including organization name
            const res = await api.get(`/api/users/${user?._id}`);
            setProfileData(res.data);

            // Initialize form state
            if (res.data.preferences) {
                setPhotoUrl(res.data.preferences.photoUrl || '');
                if (res.data.preferences.notifications) {
                    setNotifications({
                        newCourse: res.data.preferences.notifications.newCourse ?? true,
                        evaluationOpen: res.data.preferences.notifications.evaluationOpen ?? true,
                        evaluationDue: res.data.preferences.notifications.evaluationDue ?? true,
                        evaluationGraded: res.data.preferences.notifications.evaluationGraded ?? true
                    });
                }
            }
            setLoading(false);
        } catch (err) {
            console.error("Error fetching profile", err);
            showToast("Erreur lors du chargement du profil", "error");
            setLoading(false);
        }
    };

    const handleNotificationChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setNotifications({ ...notifications, [key]: event.target.checked });
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validation
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            showToast("Format de fichier non supporté. Utilisez JPG ou PNG.", "error");
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            showToast("Le fichier est trop volumineux (max 5Mo)", "error");
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            setLoading(true); // Or use a separate uploading state
            const res = await api.post('/api/media/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setPhotoUrl(res.data.url);
            showToast("Image téléchargée avec succès", "success");

            // Refresh global user context to update header
            await refreshUser();
        } catch (err) {
            console.error("Error uploading file", err);
            showToast("Erreur lors du téléchargement de l'image", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (password && password !== confirmPassword) {
            showToast("Les mots de passe ne correspondent pas", "error");
            return;
        }

        setSaving(true);
        try {
            const updateData: any = {
                preferences: {
                    photoUrl,
                    notifications
                }
            };

            if (password) {
                updateData.password = password;
            }

            await api.put('/api/users/profile', updateData);

            showToast("Profil mis à jour avec succès", "success");
            setPassword('');
            setConfirmPassword('');

            // Refresh profile data
            fetchProfile();

            // Refresh global user context to update header
            await refreshUser();

        } catch (err) {
            console.error("Error updating profile", err);
            showToast("Erreur lors de la mise à jour du profil", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 4 }}>Chargement...</Box>;

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700, color: '#1a237e', mb: 4 }}>
                Mon Profil
            </Typography>

            <Grid container spacing={4}>
                {/* Left Column - Avatar & Read-only Info */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 4, height: '100%' }}>
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                            <Avatar
                                src={photoUrl || undefined}
                                sx={{ width: 120, height: 120, mx: 'auto', bgcolor: 'primary.main', fontSize: '3rem' }}
                            >
                                {!photoUrl && (profileData?.firstName?.charAt(0) || <PersonIcon fontSize="large" />)}
                            </Avatar>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                            {profileData?.firstName} {profileData?.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'Quicksand, sans-serif' }}>
                            {profileData?.role === 'learner' ? 'Apprenant' : profileData?.role}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                École / Organisme
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2, fontFamily: 'Quicksand, sans-serif' }}>
                                {profileData?.organizationId?.name || "Non assigné"}
                            </Typography>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Email
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'Quicksand, sans-serif' }}>
                                {profileData?.email}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Column - Editable Forms */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 4, borderRadius: 4 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', mb: 3 }}>
                            Modifier mes informations
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Profile Image Upload */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: 'Quicksand, sans-serif' }}>
                                    Photo de profil (JPG/PNG, max 5Mo)
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<CloudUploadIcon />}
                                        sx={{ fontFamily: 'Quicksand, sans-serif', textTransform: 'none' }}
                                    >
                                        Choisir une image
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/png, image/jpeg, image/jpg"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                    {photoUrl && (
                                        <Typography variant="caption" sx={{ fontFamily: 'Quicksand, sans-serif', color: 'text.secondary', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            Image sélectionnée
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Divider />

                            {/* Password Change */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontFamily: 'Quicksand, sans-serif' }}>
                                    Changer de mot de passe
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            label="Nouveau mot de passe"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            fullWidth
                                            InputLabelProps={{ style: { fontFamily: 'Quicksand, sans-serif' } }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField
                                            label="Confirmer le mot de passe"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            fullWidth
                                            InputLabelProps={{ style: { fontFamily: 'Quicksand, sans-serif' } }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Divider />

                            {/* Notifications */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontFamily: 'Quicksand, sans-serif' }}>
                                    Notifications
                                </Typography>
                                <Grid container spacing={1}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={notifications.newCourse}
                                                    onChange={handleNotificationChange('newCourse')}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Nouveau cours disponible</Typography>}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={notifications.evaluationOpen}
                                                    onChange={handleNotificationChange('evaluationOpen')}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Évaluation ouverte</Typography>}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={notifications.evaluationDue}
                                                    onChange={handleNotificationChange('evaluationDue')}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Évaluation bientôt en retard</Typography>}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={notifications.evaluationGraded}
                                                    onChange={handleNotificationChange('evaluationGraded')}
                                                    color="primary"
                                                />
                                            }
                                            label={<Typography sx={{ fontFamily: 'Quicksand, sans-serif' }}>Évaluation corrigée</Typography>}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSubmit}
                                    disabled={saving}
                                    sx={{
                                        fontFamily: 'Quicksand, sans-serif',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        px: 4,
                                        borderRadius: 2
                                    }}
                                >
                                    {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProfilePage;
